import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Gift, QrCode as IconeQr } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { LimitesRecarga, PromocaoRecarga, Recarga } from '../../api/tipos';
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Girador,
  cx,
} from '../../componentes/ui';
import { reais } from '../../util/formato';
import { avisarSaldoMudou } from './Layout';
import css from './RecarregarPix.module.css';

/** Valores sugeridos, como na IDV. */
const SUGESTOES = [3_000, 10_000, 30_000, 100_000];

/** De quanto em quanto tempo perguntar se o Pix caiu. */
const INTERVALO_MS = 4_000;

interface Props {
  /** Chamado quando o pagamento é confirmado, para recarregar o saldo. */
  aoConfirmar: () => void;
  /**
   * Valor já preenchido, em centavos — o quanto falta para o lote que o
   * usuário está tentando iniciar. Vem do modal aberto no meio de uma busca:
   * ali ele não quer escolher um valor, quer confirmar o que já sabe que falta.
   */
  valorInicial?: number;
  /**
   * `true` abre em modo CONFIRMAÇÃO: mostra o valor já preenchido e um único
   * botão, em vez da grade de sugestões. Quem chegou aqui no meio de uma busca
   * já sabe quanto precisa — escolher de novo seria trabalho à toa.
   */
  direto?: boolean;
}

/** O cartão da tela de Créditos: mesma lógica, com moldura e título. */
export function RecarregarPix(props: Props) {
  return (
    <Cartao titulo="Adicionar crédito por Pix" descricao="O saldo entra assim que o Pix é pago.">
      <FluxoRecarga {...props} />
    </Cartao>
  );
}

/**
 * Escolher o valor → gerar o Pix → esperar cair.
 *
 * Sem moldura própria: quem usa decide se isso vive num cartão (tela de
 * Créditos) ou num diálogo (recarga no meio de uma busca). Antes o fluxo estava
 * casado com o `Cartao`, e reaproveitá-lo no modal significaria uma segunda
 * cópia da lógica de polling — duas implementações do mesmo "o Pix caiu?".
 */
export function FluxoRecarga({ aoConfirmar, valorInicial, direto }: Props) {
  const [limites, setLimites] = useState<LimitesRecarga | null>(null);
  const [valor, setValor] = useState(valorInicial ?? SUGESTOES[1]);
  const [recarga, setRecarga] = useState<Recarga | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const [copiou, setCopiou] = useState(false);
  /** No modo confirmação, `true` depois que a pessoa pede para trocar o valor. */
  const [escolhendo, setEscolhendo] = useState(false);
  /** `true` depois que a pessoa mexe no valor — a partir daí, é dela. */
  const [tocou, setTocou] = useState(false);
  const jaAvisou = useRef(false);

  useEffect(() => {
    api<LimitesRecarga>('/creditos/recargas/limites')
      .then((l) => {
        setLimites(l);
        // O que falta para o lote pode ser menos que o mínimo da recarga (faltar
        // R$ 2 não permite gerar um Pix de R$ 2). Subir para o mínimo é melhor
        // do que deixar o botão desabilitado sem o usuário entender por quê.
        //
        // Só mexe se a pessoa ainda não escolheu nada: comparar o valor atual
        // com `valorInicial` para adivinhar isso erra assim que os dois
        // coincidem por acaso.
        if (valorInicial !== undefined && !tocou) {
          setValor(Math.max(valorInicial, l.minimoCentavos));
        }
      })
      .catch(() => undefined);
    // `tocou` de fora das dependências de propósito: mexer no valor não pode
    // disparar uma nova consulta de limites.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorInicial]);

  /**
   * Enquanto o QR está aberto, pergunta ao servidor se caiu.
   *
   * O servidor, por sua vez, reconsulta o Asaas quando a recarga ainda consta
   * pendente — então o pagamento aparece mesmo se o webhook atrasar. Sem isso, a
   * tela dependeria de uma entrega externa que pode simplesmente não chegar.
   */
  useEffect(() => {
    if (!recarga || recarga.status !== 'PENDENTE') return;
    let vivo = true;

    const id = window.setInterval(() => {
      void api<Recarga>(`/creditos/recargas/${recarga.id}`)
        .then((r) => {
          if (!vivo) return;
          setRecarga(r);
          if (r.status === 'PAGA' && !jaAvisou.current) {
            jaAvisou.current = true;
            // Aqui, e não em quem usa o fluxo: tanto a tela de Créditos quanto
            // o modal de recarga precisam que a lateral mostre o saldo novo na
            // hora, em vez de esperar o próximo ciclo de atualização dela.
            avisarSaldoMudou();
            aoConfirmar();
          }
        })
        .catch(() => undefined);
    }, INTERVALO_MS);

    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [recarga, aoConfirmar]);

  async function gerar() {
    setErro(null);
    setGerando(true);
    jaAvisou.current = false;
    try {
      setRecarga(
        await api<Recarga>('/creditos/recargas', {
          metodo: 'POST',
          corpo: { valorCentavos: valor },
        }),
      );
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível gerar a cobrança agora.',
      );
    } finally {
      setGerando(false);
    }
  }

  async function copiar(texto: string) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiou(true);
      window.setTimeout(() => setCopiou(false), 2500);
    } catch {
      setErro('Não foi possível copiar. Selecione o código e copie à mão.');
    }
  }

  if (limites && !limites.disponivel) {
    return (
      <Aviso tom="info">
        A recarga por Pix está indisponível no momento. Fale com o suporte para creditar a
        conta.
      </Aviso>
    );
  }

  const abaixoDoMinimo = !!limites && valor < limites.minimoCentavos;
  const acimaDoMaximo = !!limites && valor > limites.maximoCentavos;

  return (
    <div className={css.fluxo}>
      {erro && <Aviso>{erro}</Aviso>}

      {recarga?.status === 'PAGA' ? (
        <Aviso tom="ok" titulo="Pagamento confirmado">
          {reais(recarga.valorCentavos)} creditados na sua conta.
        </Aviso>
      ) : recarga?.pixPayload ? (
        <QrCode
          recarga={recarga}
          copiou={copiou}
          aoCopiar={() => void copiar(recarga.pixPayload ?? '')}
          aoCancelar={() => setRecarga(null)}
        />
      ) : direto && !escolhendo ? (
        <Confirmacao
          valor={valor}
          minimo={limites?.minimoCentavos}
          subiuAoMinimo={
            valorInicial !== undefined &&
            !!limites &&
            valorInicial < limites.minimoCentavos
          }
          gerando={gerando}
          pronto={!!limites}
          aoGerar={() => void gerar()}
          aoTrocar={() => setEscolhendo(true)}
        />
      ) : (
        <>
          <fieldset className={css.grupo}>
            <legend className={css.legenda}>Quanto você quer adicionar?</legend>
            <div className={css.sugestoes}>
              {SUGESTOES.map((v) => {
                const bonus = bonusDe(limites?.promocao, v);
                return (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={valor === v}
                    className={css.sugestao}
                    onClick={() => {
                      setTocou(true);
                      setValor(v);
                    }}
                  >
                    {/* Só o valor. A estimativa de XMLs que ficava aqui usava um
                        preço fixo e errava fora da faixa — e o custo real já
                        aparece na hora de iniciar a busca, calculado no servidor
                        com a tabela de verdade. */}
                    <span className={cx(css.sugestaoValor, 'num')}>{reais(v)}</span>
                    {bonus.percentual > 0 && (
                      <span className={css.sugestaoBonus}>+{bonus.percentual}% de bônus</span>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <Campo
            rotulo="Ou outro valor"
            inputMode="numeric"
            value={reais(valor)}
            onChange={(e) => {
              // Digitação em centavos: o usuário digita os números e a
              // vírgula anda sozinha, sem chance de errar a casa decimal.
              const digitos = e.target.value.replace(/\D/g, '');
              setTocou(true);
              setValor(Math.min(Number(digitos || 0), 100_000_000));
            }}
            erro={
              limites && abaixoDoMinimo
                ? `O mínimo por recarga é ${reais(limites.minimoCentavos)}.`
                : limites && acimaDoMaximo
                  ? `O máximo por recarga é ${reais(limites.maximoCentavos)}.`
                  : undefined
            }
            dica={
              limites
                ? `Mínimo ${reais(limites.minimoCentavos)} · máximo ${reais(limites.maximoCentavos)}.`
                : undefined
            }
          />

          {limites?.promocao && <FaixaBonus promocao={limites.promocao} valor={valor} />}

          <div>
            <Botao
              icone={<IconeQr />}
              onClick={() => void gerar()}
              carregando={gerando}
              disabled={!limites || abaixoDoMinimo}
            >
              Gerar Pix de {reais(valor)}
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Modo confirmação: o valor já vem preenchido e só falta um clique.
 *
 * O caminho para trocar o valor continua visível — preencher não é decidir pelo
 * usuário, e às vezes ele prefere pôr um valor redondo e deixar sobrando.
 */
function Confirmacao({
  valor,
  minimo,
  subiuAoMinimo,
  gerando,
  pronto,
  aoGerar,
  aoTrocar,
}: {
  valor: number;
  minimo?: number;
  subiuAoMinimo: boolean;
  gerando: boolean;
  pronto: boolean;
  aoGerar: () => void;
  aoTrocar: () => void;
}) {
  return (
    <div className={css.fluxo}>
      <div className={css.resumoValor}>
        <span className={css.resumoRotulo}>Valor da recarga</span>
        <span className={cx(css.resumoNumero, 'num')}>{reais(valor)}</span>
        {subiuAoMinimo && minimo !== undefined && (
          <span className={css.resumoNota}>
            Ajustado para o mínimo de {reais(minimo)} por recarga. O que sobrar fica no seu
            saldo para a próxima busca.
          </span>
        )}
      </div>

      <div className={css.botoes}>
        <Botao icone={<IconeQr />} onClick={aoGerar} carregando={gerando} disabled={!pronto}>
          Gerar Pix de {reais(valor)}
        </Botao>
        <Botao aparencia="secundario" onClick={aoTrocar}>
          Outro valor
        </Botao>
      </div>
    </div>
  );
}

function QrCode({
  recarga,
  copiou,
  aoCopiar,
  aoCancelar,
}: {
  recarga: Recarga;
  copiou: boolean;
  aoCopiar: () => void;
  aoCancelar: () => void;
}) {
  return (
    <div className={css.fluxo}>
      <div className={css.pix}>
        {recarga.pixQrCode && (
          <img
            src={`data:image/png;base64,${recarga.pixQrCode}`}
            alt="QR Code do Pix"
            width={176}
            height={176}
            className={css.qr}
          />
        )}

        <div className={css.pixDados}>
          <div>
            <span className={css.resumoRotulo}>Valor do Pix</span>
            <div className={cx(css.pixValor, 'num')}>{reais(recarga.valorCentavos)}</div>
            <p className={css.pixInstrucao}>
              Aponte a câmera do app do seu banco para o QR Code, ou use o Pix copia e cola.
            </p>
          </div>

          <code className={css.copiaECola} aria-label="Código Pix copia e cola">
            {recarga.pixPayload}
          </code>

          <div className={css.botoes}>
            <Botao pequeno icone={copiou ? <Check /> : <Copy />} onClick={aoCopiar}>
              {copiou ? 'Código copiado' : 'Copiar código Pix'}
            </Botao>
            <Botao aparencia="secundario" pequeno onClick={aoCancelar}>
              Gerar outro valor
            </Botao>
          </div>
        </div>
      </div>

      <div className={css.aguardando} role="status">
        <Girador tamanho={14} />
        <span>
          Aguardando o pagamento. Assim que o Pix cair, o saldo entra sozinho — pode deixar
          esta tela aberta.
        </span>
      </div>

      {recarga.expiraEm && (
        <span className={css.validade}>
          Este código vale até {new Date(recarga.expiraEm).toLocaleString('pt-BR')}.
        </span>
      )}
    </div>
  );
}

/**
 * Bônus que a promoção dá para `valor`. Mesma regra do servidor (faixa em que
 * o valor cai, arredondado para baixo) — o crédito de verdade é calculado lá;
 * aqui é só para a pessoa ver antes de pagar.
 */
function bonusDe(
  promocao: PromocaoRecarga | null | undefined,
  valor: number,
): { percentual: number; centavos: number } {
  const faixa = promocao?.faixas
    .filter((f) => valor >= f.minimoCentavos)
    .at(-1);
  if (!faixa) return { percentual: 0, centavos: 0 };
  return {
    percentual: faixa.percentual,
    centavos: Math.floor((valor * faixa.percentual) / 100),
  };
}

/** "R$ 30 + R$ 6 de bônus = R$ 36", com a tabela de faixas logo abaixo. */
function FaixaBonus({
  promocao,
  valor,
}: {
  promocao: PromocaoRecarga;
  valor: number;
}) {
  const bonus = bonusDe(promocao, valor);
  const [ano, mes, dia] = promocao.ate.split('-');
  const proxima = promocao.faixas.find((f) => f.minimoCentavos > valor);

  return (
    <div className={css.bonus}>
      <Gift className={css.bonusIcone} aria-hidden="true" />
      <div className={css.bonusTexto}>
        {bonus.centavos > 0 ? (
          <span>
            <strong>
              {reais(valor)} + {reais(bonus.centavos)} de bônus ={' '}
              {reais(valor + bonus.centavos)}
            </strong>{' '}
            ({bonus.percentual}% a mais).
          </span>
        ) : (
          <span>
            <strong>Promoção:</strong> recargas a partir de{' '}
            {reais(promocao.faixas[0].minimoCentavos)} ganham {promocao.faixas[0].percentual}% de
            bônus.
          </span>
        )}
        {proxima && (
          <span>
            A partir de {reais(proxima.minimoCentavos)}, o bônus sobe para {proxima.percentual}%.
          </span>
        )}
        <span className={css.bonusFaixas}>
          {promocao.faixas
            .map((f) => `${reais(f.minimoCentavos)} ou mais: ${f.percentual}%`)
            .join(' · ')}{' '}
          — válido para Pix gerado até {dia}/{mes}/{ano}.
        </span>
      </div>
    </div>
  );
}
